import React, { useEffect, useState } from 'react';
import { push } from "connected-react-router";
import { useDispatch } from 'react-redux';
import { reduxForm, destroy, initialize, change } from 'redux-form';

import { CREATIVE_ASSET_TYPES, SOCIAL_CONCEPT_TYPE } from 'utils/constants';
import { SOCIAL_WIZARD } from 'widgets/forms/list';

import { CreativeDetails } from "reducers/CreativeDetails";
import { clearConcept, loadConcept, saveNewSocial2Concept, addCreativesToSocial2Concept } from 'reducers/ConceptDetails';

import FormatSelectModal from "./FormatSelectModal";
import AddContentModal from "./AddContentModal";

import { warnAll, validateAll } from './validators/validators';
import { serializeByAdType } from 'social/serializers';
import { conceptNameExists } from 'widgets/forms/validators/Concepts';
import { isBuildMethodSupported } from "social/utils";
import { clearImposiumProjects, getImposiumProject } from "reducers/ImposiumProjects";
import { clearCompositions } from "reducers/ImposiumCompositions";
import { clearAEPackages } from "reducers/AfterEffectsPackages";

import "./SocialModal.css";

const ModalsByStep = [
	FormatSelectModal,
	AddContentModal,
];

function SocialModal(props) {
	const dispatch = useDispatch();
	const { libraryId, conceptId, returnPath, asyncValidating, dirty } = props;
	const [breadcrumbStep, setBreadcrumbStep] = useState(0);

	useEffect(() => {
		dispatch(CreativeDetails.clearDetails()); // previously loaded details can break some dynamic features

		// We have to destroy form state manually because destroyOnUnmount is false for the wizard
		dispatch(destroy(SOCIAL_WIZARD));
		return () => {
			dispatch(destroy(SOCIAL_WIZARD));
			dispatch(clearImposiumProjects());
			dispatch(clearCompositions());
			dispatch(clearAEPackages());
			dispatch(CreativeDetails.clearDetails());
		};
	}, []);

	useEffect(() => {
		if(libraryId && conceptId){
			const initialValues = { conceptId };
			dispatch(clearConcept(conceptId));

			dispatch(loadConcept(libraryId, conceptId))
				.then((response) => {
					if(response?.success){
						const { creatives = []} = response?.payload?.data || {};
						const imposiumCreative = creatives.find(creative => creative.assetType === CREATIVE_ASSET_TYPES.IMPOSIUM);

						if(imposiumCreative){
							return dispatch(CreativeDetails.loadCreative(imposiumCreative.id))
								.then((creativeResponse) => {
									const { storyId } = creativeResponse?.payload?.data || {};
									if(creativeResponse?.success && storyId){
										return dispatch(getImposiumProject(libraryId, storyId))
											.then(impProject => initialValues.conceptImposiumStory = impProject);
									}
								})
								.finally(() => {
									dispatch(CreativeDetails.clearDetails());
								});
						}
					}
				})
				.finally(() => {
					// We set initial values here so as to not mark the form dirty
					dispatch(initialize(SOCIAL_WIZARD, initialValues));
				});
		}
	}, [libraryId, conceptId]);

	const maxStep = ModalsByStep.length - 1;

	function incrementStep(){
		if(breadcrumbStep < maxStep){
			setBreadcrumbStep(breadcrumbStep + 1);
		}
	}

	function decrementStep(){
		if(breadcrumbStep > 0){
			setBreadcrumbStep(breadcrumbStep - 1);
		}
	}

	function closeModal(){
		let shouldClose = true;

		if (dirty) {
			let confirmMessage = "Are you sure you want to cancel concept creation? All data entered will be lost.";
			if (conceptId) {
				confirmMessage = "Are you sure you want to cancel adding new creative? All data entered will be lost.";
			}

			shouldClose = confirm(confirmMessage);
		}

		if (!shouldClose) { return; }

		dispatch(push(returnPath));
	}

	const Modal = ModalsByStep[breadcrumbStep];

	return (
		<Modal
			asyncValidating = {asyncValidating}
			onClose = {closeModal}
			onNext = {incrementStep}
			onBack = {decrementStep}
		/>
	);
}

function asyncValidate(values, dispatch, props){
	const { conceptName } = values || {};
	const { libraryId, conceptId } = props || {};

	if(!conceptId){
		return conceptNameExists(dispatch, libraryId, conceptName, SOCIAL_CONCEPT_TYPE);
	} else {
		return Promise.resolve();
	}
}

export default reduxForm({
	form: SOCIAL_WIZARD,

	// destroyOnUnmount is false to keep fields alive as we cycle through wizard subsections
	destroyOnUnmount: false,

	warn: warnAll,
	validate: validateAll,
	asyncValidate,
	asyncBlurFields: ["conceptName"],

	onChange(values, dispatch){
		const { adTypes = [], shared = {} } = values;
		adTypes.forEach((adType) => {
			const adTypeValues= values[adType];
			const { isDynamic, assetType } = adTypeValues || {};
			if(!isBuildMethodSupported(assetType, { adType, isDynamic })){
				dispatch(change(SOCIAL_WIZARD, `${adType}.assetType`, CREATIVE_ASSET_TYPES.PREBUILT));
			}
		});
		const isAESupportedForSelection = adTypes.some(
			type => isBuildMethodSupported(CREATIVE_ASSET_TYPES.AFTER_EFFECTS, { adType: type, isDynamic: values[type]?.isDynamic })
		);
		if(!isAESupportedForSelection && shared.assetType === CREATIVE_ASSET_TYPES.AFTER_EFFECTS){
			dispatch(change(SOCIAL_WIZARD, "shared.assetType", CREATIVE_ASSET_TYPES.PREBUILT));
		}
	},

	onSubmit(values, dispatch, formProps){
		const { libraryId, conceptId, returnPath, socialState={} } = formProps;
		const { adTypes, conceptName } = values;

		const creatives = adTypes.map((adType) => {
			return serializeByAdType(values, adType, socialState);
		});

		const concept = {
			conceptType: "social",
			conceptName,
			creatives,
		};

		if(conceptId){
			dispatch(addCreativesToSocial2Concept(libraryId, conceptId, concept, adTypes, true)).then((response) => {
				if(response?.success){
					dispatch(push(returnPath));
				}
			});
		} else {
			dispatch(saveNewSocial2Concept(libraryId, concept, true)).then((response) => {
				if(response?.success){
					dispatch(push(returnPath));
				}
			});
		}
	},
})(SocialModal);

SocialModal.displayName = "SocialModal";
